import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import CustomModal from '../CustomModal/CustomModal';
import CustomInput from '../CustomInput/CustomInput';
import CustomTextArea from '../CustomTextArea/CustomTextArea';
import DatePickerInput from '../DatePickerInput/DatePickerInput';
import Button from '../Button/Button';
import NotificationSnackbar from '../NotificationSnackbar/NotificationSnackbar';
import KnowledgeBaseService from '../../services/KnowledgeBaseService/KnowledgeBaseService';
import { KnowledgeBaseItemResponse } from '../../types/KnowledgeBaseItemResponse';

interface FormValues {
  name: string;
  description: string | null;
}

interface EditKnowledgeBaseItemModalProps {
  isOpen: boolean;
  item: KnowledgeBaseItemResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

function EditKnowledgeBaseItemModal({
  isOpen,
  item,
  onClose,
  onSuccess
}: Readonly<EditKnowledgeBaseItemModalProps>): React.ReactElement {
  const [nextReviewDate, setNextReviewDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const { currentChatbot } = useContext(ChatbotContext);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      'Name is required for your knowledge base item'
    ),
    description: Yup.string().nullable()
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: '', description: '' }
  });

  useEffect(() => {
    if (item && isOpen) {
      reset({
        name: item.name,
        description: item.description ?? ''
      });
      if (item.nextReviewDate) {
        setNextReviewDate(dayjs(item.nextReviewDate).toDate());
      } else {
        setNextReviewDate(null);
      }
    }
  }, [item, isOpen, reset]);

  const handleClose = (): void => {
    setNextReviewDate(null);
    reset();
    setErrorMessage('');
    onClose();
  };

  const onSubmit = (data: FormValues): void => {
    setIsLoading(true);
    setErrorMessage('');

    if (!item || !currentChatbot) {
      setErrorMessage('Unable to update knowledge base item');
      setShowError(true);
      setIsLoading(false);
      return;
    }

    KnowledgeBaseService.updateKnowledgeBaseItem(
      currentChatbot.id,
      item.id,
      data.name,
      data.description,
      nextReviewDate,
      item.enabled
    )
      .then(() => {
        setSuccessMessage('Item updated successfully');
        setShowSuccess(true);
        onSuccess();
        handleClose();
      })
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        const error =
          err.apiError?.message ||
          'Something went wrong. Please try again later.';
        setErrorMessage(error);
        setShowError(true);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <CustomModal
        open={isOpen}
        onClose={handleClose}
        title='Edit document'
        maxWidth='md'
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className='space-y-4'
        >
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <CustomInput
                label='Item name'
                type='name'
                {...field}
                error={errors.name}
                disabled={item?.websiteSourceId != null}
                helperText={
                  item?.websiteSourceId != null
                    ? 'Crawled pages take their name from the page title.'
                    : undefined
                }
              />
            )}
          />
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <CustomTextArea
                label='Description'
                type='description'
                {...field}
                value={field.value ?? ''}
                error={errors.description}
              />
            )}
          />
          <DatePickerInput
            label='Next review date'
            date={nextReviewDate ?? undefined}
            setDate={(date) => setNextReviewDate(date ?? null)}
          />
          <div className='flex justify-end gap-2 pt-4'>
            <Button type='button' variant='outline' onClick={handleClose}>
              Cancel
            </Button>
            <Button type='submit' loading={isLoading}>
              Save changes
            </Button>
          </div>
        </form>
      </CustomModal>

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </>
  );
}

export default EditKnowledgeBaseItemModal;
